// NodeTagsModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { getNodeTypeTagSuggestions } from 'services/nodes/NodeReadService';

// CSS
import './NodeTagsModal.css';

// Components
import NodeTags from './NodeTags';
import Modal from 'components/modal/Modal';

/**
 * NodeTagsModal
 * 
 * Modal for editing the node's tags.
 * 
 * @param {object} node - Node object.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onTagsSaved - Callback when tags is saved.
 * 
 * @returns {ReactNode} - NodeTagsModal component.
 */
const NodeTagsModal = ({ node, isOpen, setOpen, onTagsSaved }) => {

    // ----- STATE ----- //

    // State

    const [tags, setTags] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    // ----- DEVICE DETECTION ----- //

    // Device Detection

    const isMobile = useIsMobile();

    // ----- EFFECTS ----- //

    // Sync tags when modal opens or node changes

    useEffect(() => {
        if (isOpen) {
            setTags(node?.tags || []);
        }
    }, [isOpen, node]);

    useEffect(() => {
        if (!isOpen) {
            setSuggestions([]);
            setSuggestionsLoading(false);
            return;
        }

        const baseKey = typeof node?.baseKey === 'string' ? node.baseKey.trim() : '';
        const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';

        if (!baseKey || !typeKey) {
            setSuggestions([]);
            setSuggestionsLoading(false);
            return;
        }

        let isCancelled = false;

        const loadSuggestions = async () => {
            setSuggestionsLoading(true);

            try {
                const nextSuggestions = await getNodeTypeTagSuggestions({
                    baseKey,
                    typeKey,
                    excludeNodeKey: node?.key,
                });

                if (!isCancelled) {
                    setSuggestions(nextSuggestions);
                }
            } catch (error) {
                console.error('[NodeTagsModal] tag suggestions error:', error);

                if (!isCancelled) {
                    setSuggestions([]);
                }
            } finally {
                if (!isCancelled) {
                    setSuggestionsLoading(false);
                }
            }
        };

        loadSuggestions();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, node?.key, node?.type, node?.baseKey]);

    // ----- HANDLERS ----- //

    /**
     * Handles saving the tags.
     */

    const handleSave = (savedTags) => {
        onTagsSaved(savedTags);
        setOpen(false);
    };

    /**
     * Handles canceling the edit.
     */
    const handleCancel = () => {
        setOpen(false);
    };

    // ----- RENDER ----- //

    return (

        <Modal
          title="Tags"
          isOpen={isOpen}
          width={isMobile ? '100%' : 460}
          height="auto"
          onClose={() => setOpen(false)}
        >

            {/* WRAPPER */}
            <div className="node-tags-modal-wrapper">

                <NodeTags
                  tags={tags}
                  suggestions={suggestions}
                  suggestionsLoading={suggestionsLoading}
                  onCancel={handleCancel}
                  onSave={handleSave}
                />

            </div>

        </Modal>
    );
};

// Dev Tools
NodeTagsModal.displayName = 'NodeTagsModal';

export default NodeTagsModal;
